import type {
  T_OnboardingIngestChoice,
  T_OnboardingV2SourceSelection,
} from "@lattice/core-lib/types/onboarding/onboarding.types";
import type { T_PortfolioDraftHolding } from "../portfolio/portfolio-draft-machine";
import {
  E_OnboardingV2Step,
  type T_OnboardingV2Step,
} from "./onboarding-v2-analytics";
import type { EventObject, MetaObject, SetupTypes, SnapshotFrom } from "xstate";
import { assign, fromPromise, setup } from "xstate";

export type T_OnboardingStepsDestination = "shell" | "trial_offer";

export type T_OnboardingStepsInput = {
  complete: () => Promise<void>;
  onCompleted: (destination: T_OnboardingStepsDestination) => void;
  recordIngestChoice: (choice: T_OnboardingIngestChoice) => Promise<void>;
  recordSource: (source: T_OnboardingV2SourceSelection) => Promise<void>;
};

type T_OnboardingStepsContext = {
  completionDestination: T_OnboardingStepsDestination | null;
  error: string | null;
  ingestChoice: T_OnboardingIngestChoice | null;
  input: T_OnboardingStepsInput;
  manualEditorHolding: T_PortfolioDraftHolding | null | undefined;
  sourceSelection: T_OnboardingV2SourceSelection | null;
  uploadEditorOpen: boolean;
};

export type T_OnboardingStepsEvent =
  | { type: "BACK" }
  | { type: "BROKERAGE_CONNECTED" }
  | { type: "CHANGE_SOURCE" }
  | { type: "CLOSE_MANUAL_EDITOR" }
  | { type: "CLOSE_UPLOAD_EDITOR" }
  | { type: "HOLDINGS_AVAILABLE" }
  | { type: "HOLDINGS_EMPTY" }
  | {
      choice: Extract<T_OnboardingIngestChoice, "FILE" | "MANUAL" | "PHOTO">;
      holding?: T_PortfolioDraftHolding | null;
      type: "CHOOSE_PORTFOLIO_METHOD";
    }
  | {
      choice: T_OnboardingIngestChoice;
      type: "CONTINUE_TO_TRIAL";
    }
  | {
      choice: T_OnboardingIngestChoice;
      type: "INGEST_CHOICE_RECORDED";
    }
  | { type: "RETRY" }
  | {
      selection: T_OnboardingV2SourceSelection;
      type: "SELECT_SOURCE";
    }
  | { type: "SKIP_TRIAL" }
  | { type: "START_TRIAL" };

type T_RecordSourceActorInput = {
  recordSource: T_OnboardingStepsInput["recordSource"];
  source: T_OnboardingV2SourceSelection | null;
};

type T_RecordIngestChoiceActorInput = {
  choice: T_OnboardingIngestChoice | null;
  recordIngestChoice: T_OnboardingStepsInput["recordIngestChoice"];
};

type T_CompleteActorInput = {
  complete: T_OnboardingStepsInput["complete"];
};

const types: SetupTypes<
  T_OnboardingStepsContext,
  T_OnboardingStepsEvent,
  Record<never, string>,
  string,
  T_OnboardingStepsInput,
  unknown,
  EventObject,
  MetaObject
> = {};

function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

export const onboardingStepsMachine = setup({
  types,
  actors: {
    complete: fromPromise<undefined, T_CompleteActorInput>(
      async ({ input }) => {
        await input.complete();
        return undefined;
      },
    ),
    recordIngestChoice: fromPromise<undefined, T_RecordIngestChoiceActorInput>(
      async ({ input }) => {
        if (input.choice === null) throw new Error("Choose a method first.");
        await input.recordIngestChoice(input.choice);
        return undefined;
      },
    ),
    recordSource: fromPromise<undefined, T_RecordSourceActorInput>(
      async ({ input }) => {
        if (input.source === null) throw new Error("Choose an option first.");
        await input.recordSource(input.source);
        return undefined;
      },
    ),
  },
  actions: {
    applySource: assign({
      error: null,
      sourceSelection: ({ event }) =>
        event.type === "SELECT_SOURCE" ? event.selection : null,
    }),
    clearError: assign({ error: null }),
    clearManualEditor: assign({ manualEditorHolding: undefined }),
    clearUploadEditor: assign({ uploadEditorOpen: false }),
    clearSource: assign({ error: null, sourceSelection: null }),
    notifyCompleted: ({ context }) => {
      if (context.completionDestination !== null) {
        context.input.onCompleted(context.completionDestination);
      }
    },
    selectIngestChoice: assign({
      error: null,
      ingestChoice: ({ event }) => {
        if (event.type === "BROKERAGE_CONNECTED") return "SNAPTRADE";
        if (
          event.type === "CHOOSE_PORTFOLIO_METHOD" ||
          event.type === "CONTINUE_TO_TRIAL" ||
          event.type === "INGEST_CHOICE_RECORDED"
        ) {
          return event.choice;
        }
        return null;
      },
      manualEditorHolding: ({ context, event }) => {
        if (event.type !== "CHOOSE_PORTFOLIO_METHOD") {
          return context.manualEditorHolding;
        }
        return event.choice === "MANUAL" ? (event.holding ?? null) : undefined;
      },
      uploadEditorOpen: ({ context, event }) => {
        if (event.type !== "CHOOSE_PORTFOLIO_METHOD") {
          return context.uploadEditorOpen;
        }
        return event.choice === "FILE" || event.choice === "PHOTO";
      },
    }),
    selectTrialOfferDestination: assign({
      completionDestination: "trial_offer",
      error: null,
    }),
    selectShellDestination: assign({
      completionDestination: "shell",
      error: null,
    }),
    setError: assign({
      error: (_, params: { error: unknown }) => getErrorMessage(params.error),
    }),
  },
}).createMachine({
  id: "onboardingSteps",
  context: ({ input }) => ({
    completionDestination: null,
    error: null,
    ingestChoice: null,
    input,
    manualEditorHolding: undefined,
    sourceSelection: null,
    uploadEditorOpen: false,
  }),
  initial: "source",
  on: {
    CLOSE_MANUAL_EDITOR: { actions: "clearManualEditor" },
    CLOSE_UPLOAD_EDITOR: { actions: "clearUploadEditor" },
  },
  states: {
    source: {
      on: {
        SELECT_SOURCE: {
          actions: "applySource",
          target: "savingSource",
        },
      },
    },
    savingSource: {
      invoke: {
        src: "recordSource",
        input: ({ context }) => ({
          recordSource: context.input.recordSource,
          source: context.sourceSelection,
        }),
        onDone: { actions: "clearError", target: "routingAccountState" },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "sourceFailed",
        },
      },
    },
    sourceFailed: {
      on: {
        CHANGE_SOURCE: { actions: "clearSource", target: "source" },
        RETRY: { actions: "clearError", target: "savingSource" },
      },
    },
    routingAccountState: {
      on: {
        HOLDINGS_AVAILABLE: { target: "review" },
        HOLDINGS_EMPTY: { target: "connect" },
      },
    },
    connect: {
      on: {
        BACK: { target: "source" },
        BROKERAGE_CONNECTED: {
          actions: "selectIngestChoice",
          target: "savingConnectChoice",
        },
        CHOOSE_PORTFOLIO_METHOD: {
          actions: "selectIngestChoice",
          target: "savingConnectChoice",
        },
        HOLDINGS_AVAILABLE: { target: "review" },
        INGEST_CHOICE_RECORDED: { actions: "selectIngestChoice" },
      },
    },
    savingConnectChoice: {
      invoke: {
        src: "recordIngestChoice",
        input: ({ context }) => ({
          choice: context.ingestChoice,
          recordIngestChoice: context.input.recordIngestChoice,
        }),
        onDone: { actions: "clearError", target: "connect" },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "connectChoiceFailed",
        },
      },
    },
    connectChoiceFailed: {
      on: {
        BACK: { actions: "clearError", target: "source" },
        BROKERAGE_CONNECTED: {
          actions: "selectIngestChoice",
          target: "savingConnectChoice",
        },
        // A failed choice must not strand the step: choosing another import
        // method is itself the recovery, so it retries rather than dead-ends.
        CHOOSE_PORTFOLIO_METHOD: {
          actions: "selectIngestChoice",
          target: "savingConnectChoice",
        },
        HOLDINGS_AVAILABLE: { actions: "clearError", target: "review" },
        RETRY: { actions: "clearError", target: "savingConnectChoice" },
      },
    },
    review: {
      on: {
        BACK: { target: "source" },
        BROKERAGE_CONNECTED: {
          actions: "selectIngestChoice",
          target: "savingAdditionalChoice",
        },
        CHOOSE_PORTFOLIO_METHOD: {
          actions: "selectIngestChoice",
          target: "savingAdditionalChoice",
        },
        CONTINUE_TO_TRIAL: {
          actions: "selectIngestChoice",
          target: "savingReviewChoice",
        },
        HOLDINGS_EMPTY: { target: "connect" },
        INGEST_CHOICE_RECORDED: { actions: "selectIngestChoice" },
      },
    },
    savingAdditionalChoice: {
      invoke: {
        src: "recordIngestChoice",
        input: ({ context }) => ({
          choice: context.ingestChoice,
          recordIngestChoice: context.input.recordIngestChoice,
        }),
        onDone: { actions: "clearError", target: "review" },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "additionalChoiceFailed",
        },
      },
    },
    additionalChoiceFailed: {
      on: {
        BACK: { actions: "clearError", target: "source" },
        BROKERAGE_CONNECTED: {
          actions: "selectIngestChoice",
          target: "savingAdditionalChoice",
        },
        CHOOSE_PORTFOLIO_METHOD: {
          actions: "selectIngestChoice",
          target: "savingAdditionalChoice",
        },
        CONTINUE_TO_TRIAL: {
          actions: "selectIngestChoice",
          target: "savingReviewChoice",
        },
        HOLDINGS_EMPTY: { actions: "clearError", target: "connect" },
        RETRY: { actions: "clearError", target: "savingAdditionalChoice" },
      },
    },
    savingReviewChoice: {
      invoke: {
        src: "recordIngestChoice",
        input: ({ context }) => ({
          choice: context.ingestChoice,
          recordIngestChoice: context.input.recordIngestChoice,
        }),
        onDone: { actions: "clearError", target: "trial" },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "reviewChoiceFailed",
        },
      },
    },
    reviewChoiceFailed: {
      on: {
        BACK: { actions: "clearError", target: "source" },
        HOLDINGS_EMPTY: { actions: "clearError", target: "connect" },
        RETRY: { actions: "clearError", target: "savingReviewChoice" },
      },
    },
    trial: {
      on: {
        BACK: { target: "review" },
        SKIP_TRIAL: {
          actions: "selectShellDestination",
          target: "completing",
        },
        START_TRIAL: {
          actions: "selectTrialOfferDestination",
          target: "completing",
        },
      },
    },
    completing: {
      invoke: {
        src: "complete",
        input: ({ context }) => ({ complete: context.input.complete }),
        onDone: { actions: "notifyCompleted", target: "completed" },
        onError: {
          actions: {
            type: "setError",
            params: ({ event }) => ({ error: event.error }),
          },
          target: "completionFailed",
        },
      },
    },
    completionFailed: {
      on: {
        RETRY: { actions: "clearError", target: "completing" },
      },
    },
    completed: { type: "final" },
  },
});

export type T_OnboardingStepsSnapshot = SnapshotFrom<
  typeof onboardingStepsMachine
>;

export function getOnboardingStep(
  snapshot: T_OnboardingStepsSnapshot,
): 1 | 2 | 3 | 4 {
  if (
    snapshot.matches("source") ||
    snapshot.matches("savingSource") ||
    snapshot.matches("sourceFailed") ||
    snapshot.matches("routingAccountState")
  ) {
    return 1;
  }
  if (
    snapshot.matches("connect") ||
    snapshot.matches("savingConnectChoice") ||
    snapshot.matches("connectChoiceFailed")
  ) {
    return 2;
  }
  if (
    snapshot.matches("review") ||
    snapshot.matches("savingAdditionalChoice") ||
    snapshot.matches("additionalChoiceFailed") ||
    snapshot.matches("savingReviewChoice") ||
    snapshot.matches("reviewChoiceFailed")
  ) {
    return 3;
  }
  return 4;
}

export function getOnboardingAnalyticsStep(
  step: 1 | 2 | 3 | 4,
): T_OnboardingV2Step {
  switch (step) {
    case 1:
      return E_OnboardingV2Step.HeardAboutUs;
    case 2:
      return E_OnboardingV2Step.HoldingsIngestChoice;
    case 3:
      return E_OnboardingV2Step.HoldingsHandoff;
    case 4:
      return E_OnboardingV2Step.Completion;
  }
}

export function getOnboardingHoldingsGuardEvent({
  holdingCount,
  snapshot,
}: {
  holdingCount: number;
  snapshot: T_OnboardingStepsSnapshot;
}): "HOLDINGS_AVAILABLE" | "HOLDINGS_EMPTY" | null {
  if (snapshot.matches("routingAccountState")) {
    return holdingCount > 0 ? "HOLDINGS_AVAILABLE" : "HOLDINGS_EMPTY";
  }
  const step = getOnboardingStep(snapshot);
  if (step === 2 && holdingCount > 0) return "HOLDINGS_AVAILABLE";
  if (step === 3 && holdingCount === 0) return "HOLDINGS_EMPTY";
  return null;
}
